import React from 'react';
import { createPortal } from 'react-dom';
import { getApiBase } from './apiBase';
import ThinkingTimer from './ThinkingTimer';
import { ResizeEdges, renderEmph } from './ariaBoxKit';
import { canPopoutChat, popoutChat } from './popoutChat';

// SetupHelpBox — the SAME support-chat box used everywhere (AriaDock /
// AssistChatBox), popped up when the owner clicks "Set up". Aria greets with a
// SHORT line + a clear action button (e.g. "Connect Google Calendar") — no
// paragraphs to read. Always has a mic so they can talk to her. It's a real
// chat to her local brain (/api/ai/conversation, text path), backed by the app
// operator manual, so she answers setup questions accurately (no hallucination).

function ownerIdLocal() {
  try { return (typeof localStorage !== 'undefined' && (localStorage.getItem('aria_user_id') || localStorage.getItem('howto_owner_id'))) || ''; } catch (_) { return ''; }
}

const MicIcon = ({ on }) => (
  <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={on ? 2.6 : 2.2} strokeLinecap="round" strokeLinejoin="round">
    <rect x="9" y="2" width="6" height="12" rx="3" />
    <path d="M5 10a7 7 0 0 0 14 0M12 19v3" />
  </svg>
);

// Default reply extractor for generic brain responses
function defaultExtractReply(data) {
  return String((data && (data.reply || data.question || data.reasoning)) || '').trim() || "I'm here — say that another way?";
}

// Intake reply extractor — collapses recognition + first question into one line
function intakeExtractReply(data) {
  const rec = data && data.recognition;
  const qs = Array.isArray(data && data.questions) ? data.questions : [];
  if (data && data.stage === 'ask-business') return (rec && rec.ask) || "What kind of work does the business do?";
  if (data && data.stage === 'interview') {
    const confirm = (rec && rec.confirm) || '';
    const firstQ = qs[0] && qs[0].ask ? qs[0].ask : '';
    if (confirm && firstQ && qs[0] && qs[0].id === 'type') return firstQ;
    return [confirm, firstQ].filter(Boolean).join(' ');
  }
  if (data && data.stage === 'ready') return "I've got everything I need — go ahead and generate your plan below!";
  return defaultExtractReply(data);
}

// localResponder(text, history) — when provided, send() calls it INSTEAD of the
// network: return a string → that's Aria's reply (appended instantly); return
// null/undefined → fall through to the normal endpoint fetch. Lets a parent run
// a scripted question queue (Business+ pipeline) inside the same chat box.
// boxRef — optional ref exposing { addAssistant(text) } so the parent can have
// Aria say something outside the send cycle (e.g. narrating scan results).
function SetupHelpBoxInner({ open, onClose, greeting, corner = false, agentTitle = '', ownerId, actions = [], checklist = null, preChoice = null, previewNode = null, confirmLabel = '', onUserMessage = null, onDraftChange = null, inputPlaceholder = '', readyAction = null, onAssist = null, endpoint = null, buildBody = null, extractReply = null, localResponder = null, onChoose = null, pinTopRight = false, onDone = null }, boxRef) {
  const [messages, setMessages] = React.useState([]); // { role, content }
  const [plusOpen, setPlusOpen] = React.useState(false); // + menu: files / assist
  const fileRef = React.useRef(null);
  // (was `isDesktop` here - its two readers, the + menu's Assist button and the
  // tear-off guard, both stopped branching on the platform on 9/7.)
  const [input, setInput] = React.useState('');
  const [sending, setSending] = React.useState(false);
  // What she's working on right now, shown beside the dots ("Looking up
  // Open Corp Junk Removal…"). Set by the responder through onThinking.
  const [thinkingLabel, setThinkingLabel] = React.useState('');
  const [recording, setRecording] = React.useState(false);
  const [micErr, setMicErr] = React.useState('');
  // checklist = { options:[{id,label}], confirmLabel, storageKey, summaryPrefix }
  const [selected, setSelected] = React.useState(() => {
    try { const k = checklist && checklist.storageKey; if (k) { const s = JSON.parse(localStorage.getItem(k) || 'null'); if (Array.isArray(s)) return new Set(s); } } catch (_) {}
    return new Set();
  });
  const [checklistDone, setChecklistDone] = React.useState(false);
  // preChoice = { question, options:[{id,label,summary}], storageKey } — a single-select
  // (e.g. "Do you allow financing?") shown ABOVE the checklist.
  const [preChoiceVal, setPreChoiceVal] = React.useState(() => {
    try { const k = preChoice && preChoice.storageKey; if (k) { const v = localStorage.getItem(k); if (v) return v; } } catch (_) {}
    return '';
  });
  const scrollRef = React.useRef(null);
  const inputRef = React.useRef(null);
  const mediaRef = React.useRef(null);
  const streamRef = React.useRef(null);
  const chunksRef = React.useRef([]);

  // Parent-driven Aria lines (pipeline narration, next questions).
  React.useImperativeHandle(boxRef, () => ({
    addAssistant: (text) => { const t = String(text || '').trim(); if (t) setMessages((m) => [...m, { role: 'assistant', content: t }]); },
  }), []);

  const greetText = greeting || `Need some help? I can walk you through getting ${agentTitle || 'this'} set up — just ask or tap below.`;

  // Geometry: move by the header, resize by ANY edge/corner (that edge moves).
  // geom null → centered at the default size. Restores the user's last size.
  const [geom, setGeom] = React.useState(() => {
    try { const w = localStorage.getItem('aria_box_w'); const h = localStorage.getItem('aria_box_h');
      if (w || h) {
        // Clamp the restored size to the CURRENT viewport — a size saved on a
        // bigger screen must never make the box taller/wider than this screen.
        const maxW = (typeof window !== 'undefined' ? window.innerWidth : 1200) - 24;
        const maxH = (typeof window !== 'undefined' ? window.innerHeight : 800) - 48;
        return { left: null, top: null,
          width: w ? Math.min(parseInt(w, 10), maxW) : undefined,
          height: h ? Math.min(parseInt(h, 10), maxH) : undefined };
      }
    } catch (_) {}
    return null;
  });
  const persistSize = (g) => { try { if (g && g.width) localStorage.setItem('aria_box_w', Math.round(g.width) + 'px'); if (g && g.height) localStorage.setItem('aria_box_h', Math.round(g.height) + 'px'); } catch (_) {} };
  // PERF: during a drag/resize we mutate the box element's style DIRECTLY (no
  // React re-render per frame) and only commit to geom state on release. This
  // is what makes dragging feel instant instead of laggy.
  const dragBoxRef = React.useRef(null);
  const applyRect = (el, l, t, w, h) => {
    el.style.position = 'fixed'; el.style.transition = 'none';
    el.style.left = l + 'px'; el.style.top = t + 'px'; el.style.right = 'auto'; el.style.bottom = 'auto';
    el.style.width = w + 'px'; el.style.height = h + 'px'; el.style.margin = '0';
  };
  const onHeaderMouseDown = (e) => {
    if (e.target.closest('.support-chat__close')) return;
    const box = e.currentTarget.closest('.support-chat'); if (!box) return;
    e.preventDefault();
    const r = box.getBoundingClientRect();
    const B = { l: r.left, t: r.top, w: r.width, h: r.height, x: e.clientX, y: e.clientY };
    let last = B;
    // Tear-off: drag the header to the window edge and release to pop the chat
    // into its own window (draggable to another screen). Both surfaces now -
    // this was the second private copy of the AriaDock gesture, guarded
    // differently; both call popoutChat.js so they cannot drift again.
    const canTear = canPopoutChat(false);
    let armed = false;
    const EDGE = 10;
    const onMove = (ev) => {
      const l = Math.max(0, Math.min(window.innerWidth - 60, B.l + (ev.clientX - B.x)));
      const t = Math.max(0, Math.min(window.innerHeight - 60, B.t + (ev.clientY - B.y)));
      last = { l, t, w: B.w, h: B.h };
      applyRect(box, l, t, B.w, B.h);
      if (canTear) {
        armed = ev.clientX <= EDGE || ev.clientX >= window.innerWidth - EDGE || ev.clientY <= EDGE || ev.clientY >= window.innerHeight - EDGE;
        box.classList.toggle('support-chat--tearing', armed);
      }
    };
    const onUp = () => {
      window.removeEventListener('pointermove', onMove); window.removeEventListener('pointerup', onUp);
      box.classList.remove('support-chat--tearing');
      // If the popup was blocked, fall through and keep the box where he put it.
      if (armed && canTear && popoutChat()) { if (onClose) onClose(); return; }
      setGeom({ left: last.l, top: last.t, width: last.w, height: last.h });
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
  };
  // Any edge/corner: the grabbed side follows the cursor (direct-DOM, no lag).
  const startResize = (dir) => (e) => {
    const box = e.currentTarget.closest('.support-chat'); if (!box) return;
    e.preventDefault(); e.stopPropagation();
    const r = box.getBoundingClientRect();
    const B = { l: r.left, t: r.top, w: r.width, h: r.height, x: e.clientX, y: e.clientY };
    let last = B;
    const move = (ev) => {
      const dx = ev.clientX - B.x, dy = ev.clientY - B.y;
      let l = B.l, t = B.t, w = B.w, h = B.h;
      if (dir.includes('e')) w = B.w + dx;
      if (dir.includes('w')) { w = B.w - dx; l = B.l + dx; }
      if (dir.includes('s')) h = B.h + dy;
      if (dir.includes('n')) { h = B.h - dy; t = B.t + dy; }
      if (w < 300) { if (dir.includes('w')) l = B.l + (B.w - 300); w = 300; }
      if (h < 360) { if (dir.includes('n')) t = B.t + (B.h - 360); h = 360; }
      w = Math.min(w, window.innerWidth - 12); h = Math.min(h, window.innerHeight - 12);
      last = { l, t, w, h };
      applyRect(box, l, t, w, h);
    };
    const up = () => { window.removeEventListener('pointermove', move); window.removeEventListener('pointerup', up); setGeom({ left: last.l, top: last.t, width: last.w, height: last.h }); persistSize(last.w && { width: last.w, height: last.h }); };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
  };
  const boxStyle = geom
    ? (geom.left != null
        ? { position: 'fixed', left: geom.left, top: geom.top, right: 'auto', bottom: 'auto', margin: 0, width: geom.width, height: geom.height, pointerEvents: 'auto' }
        : { position: 'relative', margin: 0, width: geom.width, height: geom.height, pointerEvents: 'auto' })
    : { position: 'relative', margin: 0, pointerEvents: 'auto' };

  // When the parent tells us Aria is "getting to work", slide the box to the
  // top-right corner (one-shot animated) so the workspace is visible.
  React.useEffect(() => {
    if (!pinTopRight) return;
    const box = dragBoxRef.current;
    const w = (geom && geom.width) || 380;
    const h = (geom && geom.height) || Math.min(560, window.innerHeight - 60);
    const target = { left: Math.max(12, window.innerWidth - w - 18), top: 66, width: w, height: h };
    if (box) { box.style.transition = 'left .4s cubic-bezier(.2,.8,.2,1), top .4s cubic-bezier(.2,.8,.2,1)'; setTimeout(() => { try { box.style.transition = 'none'; } catch (_) {} }, 450); }
    setGeom(target);
  }, [pinTopRight]);

  React.useEffect(() => { const el = scrollRef.current; if (el) el.scrollTop = el.scrollHeight; }, [messages, sending, open]);
  React.useEffect(() => {
    if (open && inputRef.current) { const t = setTimeout(() => { try { inputRef.current.focus(); } catch (_) {} }, 80); return () => clearTimeout(t); }
  }, [open]);
  // Stop the mic if the box closes mid-recording.
  React.useEffect(() => () => { try { (streamRef.current?.getTracks() || []).forEach((t) => t.stop()); } catch (_) {} }, []);

  const send = React.useCallback(async (override) => {
    const text = String(override != null ? override : input).trim();
    if (!text || sending) return;
    setInput('');
    const next = [...messages, { role: 'user', content: text }];
    setMessages(next);
    setSending(true);
    try { if (onUserMessage) onUserMessage(text); } catch (_) {}
    // Scripted mode — the parent answers locally (question queue); no network.
    // Pace it like a real text: the typing dots sit for a beat (~1-1.6s) before
    // her reply lands, even when the responder resolves instantly.
    if (localResponder) {
      try {
        const t0 = Date.now();
        setThinkingLabel('');
        const reply = await localResponder(text, next, setThinkingLabel);
        const minWait = 950 + Math.random() * 650;
        const elapsed = Date.now() - t0;
        if (elapsed < minWait) await new Promise((res) => setTimeout(res, minWait - elapsed));
        if (reply != null) {
          // A responder may return a string OR { text, card } — the card is
          // the business preview she renders under the question (8/13).
          const rText = (reply && typeof reply === 'object') ? reply.text : reply;
          const rCard = (reply && typeof reply === 'object') ? reply.card : null;
          if (String(rText || '').trim()) {
            setMessages((m) => [...m, { role: 'assistant', content: String(rText), ...(rCard ? { card: rCard } : {}) }]);
          }
          setThinkingLabel('');
          setSending(false);
          return;
        }
      } catch (_) { /* fall through to the normal endpoint */ }
    }
    try {
      const base = String(getApiBase() || '').replace(/\/$/, '');
      const uid = ownerId || ownerIdLocal();
      // Use override endpoint if provided (e.g. /api/aria/intake for Business+ flow),
      // otherwise fall back to the generic conversation brain.
      const apiUrl = endpoint
        ? `${base}${endpoint.startsWith('/') ? '' : '/'}${endpoint}`
        : `${base}/api/ai/conversation`;
      const history = next.slice(-6).map((m) => ({ role: m.role === 'assistant' ? 'assistant' : 'user', content: String(m.content || '') }));
      const body = buildBody
        ? buildBody(text, history, uid)
        : { initialQuery: text, prompt: text, language: 'en', inputMode: 'text', skipAudio: true, conversationHistory: history, ...(uid ? { userId: uid } : {}) };
      const res = await fetch(apiUrl, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
      let data = {};
      try { const t = await res.text(); data = t ? JSON.parse(t) : {}; } catch (_) { data = {}; }
      if (!res.ok) {
        setMessages((m) => [...m, { role: 'assistant', content: (data && (data.message || data.error)) || "I'm having trouble right now — try again in a sec.", error: true }]);
        setSending(false); return;
      }
      const _extract = extractReply || (endpoint ? intakeExtractReply : defaultExtractReply);
      let reply = _extract(data);
      setMessages((m) => [...m, { role: 'assistant', content: reply }]);
    } catch (_) {
      setMessages((m) => [...m, { role: 'assistant', content: "Can't reach me right now — check your connection and try again.", error: true }]);
    }
    setSending(false);
  }, [input, sending, messages, ownerId, onUserMessage, localResponder]);

  const toggleOpt = (id) => setSelected((prev) => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const confirmSetup = () => {
    const opts = (checklist && checklist.options) || [];
    const labels = opts.filter((o) => selected.has(o.id)).map((o) => o.label);
    try { if (checklist && checklist.storageKey) localStorage.setItem(checklist.storageKey, JSON.stringify([...selected])); } catch (_) {}
    try { if (preChoice && preChoice.storageKey && preChoiceVal) localStorage.setItem(preChoice.storageKey, preChoiceVal); } catch (_) {}
    setChecklistDone(true);
    // Setup work is DONE — tell the dashboard so the tool moves from the
    // "Recommended software" list up to Active (owner spec 2026-07-14).
    try { if (onDone) onDone(); } catch (_) {}
    const parts = [];
    if (preChoice && preChoiceVal) { const o = (preChoice.options || []).find((x) => x.id === preChoiceVal); if (o) parts.push(o.summary || o.label); }
    if (labels.length) { const prefix = (checklist && checklist.summaryPrefix) || 'Here’s what I picked'; parts.push(`${prefix}: ${labels.join(', ')}`); }
    send(parts.length ? `${parts.join('. ')}.` : 'Looks good — set it up.');
  };
  // One-click confirm for single-select choices (no Save step) — the choice
  // buttons live above the text box and apply immediately.
  const confirmChoice = (id) => {
    const o = (preChoice && preChoice.options || []).find((x) => x.id === id);
    try { if (preChoice && preChoice.storageKey) localStorage.setItem(preChoice.storageKey, id); } catch (_) {}
    setPreChoiceVal(id);
    setChecklistDone(true);
    try { if (onDone) onDone(); } catch (_) {}
    // Aria says ONE short line — NOT sent to the brain (that returned a paragraph).
    // The on-screen pointer + captions do the actual step-by-step guiding.
    const line = o ? (o.summary || o.label) : 'Sounds good.';
    setMessages((m) => [...m, { role: 'assistant', content: line }]);
    try { if (onChoose) onChoose(id); } catch (_) {}
  };

  // ── Mic: record → transcribe (same path as the main app) → send ──────────
  const transcribe = React.useCallback(async (blob, mime) => {
    const base64 = await new Promise((resolve, reject) => { const r = new FileReader(); r.onloadend = () => resolve(String(r.result || '').split(',')[1] || ''); r.onerror = () => reject(new Error('read')); r.readAsDataURL(blob); });
    if (!base64) return '';
    const billing = {}; const uid = ownerId || ownerIdLocal(); if (uid) billing.userId = uid;
    if (typeof window !== 'undefined' && window.electronAPI?.transcribeAudio) {
      const ipc = await window.electronAPI.transcribeAudio({ audio: base64, mime, ...billing });
      const data = (ipc && ipc.data) || {};
      return (ipc && ipc.ok && data.text) ? String(data.text).trim() : '';
    }
    const base = String(getApiBase() || '').replace(/\/$/, '');
    const r = await fetch(`${base}/api/speech/transcribe`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ audio: base64, mime, ...billing }) });
    const data = await r.json().catch(() => ({}));
    return (r.ok && data && data.text) ? String(data.text).trim() : '';
  }, [ownerId]);

  const toggleMic = React.useCallback(async () => {
    if (recording) { try { mediaRef.current && mediaRef.current.stop(); } catch (_) {} return; }
    setMicErr('');
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const supported = typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported;
      const mime = supported && MediaRecorder.isTypeSupported('audio/webm') ? 'audio/webm' : '';
      const mr = mime ? new MediaRecorder(stream, { mimeType: mime }) : new MediaRecorder(stream);
      chunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data && e.data.size) chunksRef.current.push(e.data); };
      mr.onstop = async () => {
        try { (streamRef.current?.getTracks() || []).forEach((t) => t.stop()); } catch (_) {}
        setRecording(false);
        const blob = new Blob(chunksRef.current, { type: mr.mimeType || 'audio/webm' });
        if (!blob.size) return;
        try { const text = await transcribe(blob, mr.mimeType || 'audio/webm'); if (text) await send(text); }
        catch (_) { setMicErr('Couldn’t hear that — try again or type.'); }
      };
      mr.start(); mediaRef.current = mr; setRecording(true);
    } catch (_) { setMicErr('Mic not available — you can type instead.'); }
  }, [recording, transcribe, send]);

  if (!open) return null;

  // Portal to <body>: rendered inside the page's styled containers, ancestor
  // transforms/filters hijack position:fixed and the box scrolls WITH the page
  // (same gotcha as the payment sheet). At the root, fixed means the SCREEN.
  return createPortal(
    <div
      className="setup-help-backdrop"
      onClick={onClose}
      // Once she's docked to the side and building, the workspace behind must
      // be fully readable AND clickable (Braeden 8/13: "the main screen needs
      // to be working... every detail"). The dimmed, blurred backdrop made his
      // own dashboard unreadable while she narrated it. Docked ⇒ corner mode:
      // no scrim, no blur, clicks pass through.
      style={(corner || pinTopRight)
        ? { position: 'fixed', inset: 0, zIndex: 1200, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'none', pointerEvents: 'none', WebkitAppRegion: 'no-drag' }
        : { position: 'fixed', inset: 0, zIndex: 1200, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20, background: 'rgba(8,8,12,0.32)', backdropFilter: 'blur(6px)', WebkitBackdropFilter: 'blur(6px)', WebkitAppRegion: 'no-drag' }}
    >
      <div
        ref={dragBoxRef}
        className={"support-chat support-chat--popup" + ((corner || pinTopRight) ? " support-chat--corner" : "")}
        role="dialog"
        aria-label="Aria setup help"
        onClick={(e) => e.stopPropagation()}
        // pointerEvents:auto — the corner backdrop passes clicks through to the
        // workspace, so the box itself has to opt back in.
        style={{ ...boxStyle, pointerEvents: 'auto' }}
      >
        <ResizeEdges start={startResize} />
        <div className="support-chat__header support-chat__header--centered" onMouseDown={onHeaderMouseDown} style={{ cursor: 'grab', userSelect: 'none' }}>
          <div className="support-chat__script">Aria</div>
          <div className="support-chat__header-actions">
            <button type="button" className="support-chat__close" onClick={onClose} aria-label="Close">×</button>
          </div>
        </div>

        <div className="support-chat__body" ref={scrollRef}>
          <div className="support-chat__bubble support-chat__bubble--assistant">{renderEmph(greetText)}</div>

          {/* Quick action buttons — what the user TAPS (no paragraph to read) */}
          {actions.length > 0 && messages.length === 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, margin: '4px 0 2px' }}>
              {actions.map((a, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => { try { a.onClick && a.onClick(); } catch (_) {} }}
                  style={{ font: 'inherit', fontSize: 14, fontWeight: 700, color: '#fff', background: 'linear-gradient(160deg,#0a84ff,#0a5fd0)', border: 'none', borderRadius: 12, padding: '12px 16px', cursor: 'pointer', textAlign: 'center', boxShadow: '0 6px 18px rgba(0,60,160,0.35)' }}
                >{a.label}</button>
              ))}
            </div>
          )}

          {/* Setup content — the question / preview / checklist list. The ACTION
              buttons live in the glossy blue bar above the text box (below). */}
          {(preChoice || (checklist && Array.isArray(checklist.options)) || previewNode) && !checklistDone && messages.length === 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, margin: '4px 0 2px' }}>
              {preChoice && preChoice.question && <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text,#1d1d1f)', margin: '2px 0' }}>{preChoice.question}</div>}
              {previewNode && <div style={{ margin: '8px 0 2px' }}>{previewNode}</div>}
              {checklist && Array.isArray(checklist.options) && (
                <>
                  {checklist.question && <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text,#1d1d1f)', margin: previewNode ? '8px 0 2px' : '2px 0' }}>{checklist.question}</div>}
                  {checklist.options.map((o) => {
                    const on = selected.has(o.id);
                    return (
                      <button key={o.id} type="button" onClick={() => toggleOpt(o.id)}
                        style={{ display: 'flex', alignItems: 'center', gap: 10, font: 'inherit', fontSize: 14, fontWeight: 600, textAlign: 'left', color: on ? '#fff' : 'var(--text,#1d1d1f)', background: on ? 'linear-gradient(160deg,#0a84ff,#0a5fd0)' : 'rgba(127,127,127,0.12)', border: 'none', borderRadius: 12, padding: '11px 14px', cursor: 'pointer' }}>
                        <span style={{ flex: 'none', width: 18, height: 18, borderRadius: 6, border: `2px solid ${on ? '#fff' : 'rgba(127,127,127,0.55)'}`, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, lineHeight: 1 }}>{on ? '✓' : ''}</span>
                        {o.label}
                      </button>
                    );
                  })}
                </>
              )}
            </div>
          )}

          {/* LIVE WORK PANEL (Braeden 8/13) — previewNode used to render only
              before the first message, so anything she builds DURING the
              conversation was invisible. It now stays pinned above the thread
              so he watches the setup fill in while she keeps asking. */}
          {previewNode && messages.length > 0 && (
            // STICKY (8/13): pinned above a growing thread, the panel scrolled
            // out of sight within two turns — he watched the chat and saw
            // "nothing". It now rides the top of the scroll area so the build
            // stays in front of him the whole conversation.
            <div style={{ position: 'sticky', top: 0, zIndex: 3, margin: '0 0 10px',
                          background: 'var(--support-bg, #fff)', paddingTop: 4, paddingBottom: 2 }}>
              {previewNode}
            </div>
          )}
          {messages.map((m, i) => (
            <React.Fragment key={i}>
              <div className={`support-chat__bubble support-chat__bubble--${m.role === 'user' ? 'user' : 'assistant'}`}>{m.role === 'user' ? m.content : renderEmph(m.content)}</div>
              {/* THE BUSINESS CARD (Braeden 8/13: "pull it up in the chat like
                  a picture, is this you?") — a real screenshot of their site
                  with the name, tagline and what they sell, so she SHOWS the
                  business she found instead of reciting a domain. */}
              {m.card && m.card.host && (
                <a className="support-chat__bizcard" href={m.card.url} target="_blank" rel="noreferrer"
                   style={{ display: 'block', textDecoration: 'none', color: 'inherit', margin: '2px 0 8px',
                            border: '1px solid rgba(255,255,255,.14)', borderRadius: 14, overflow: 'hidden',
                            background: 'rgba(255,255,255,.04)', maxWidth: 340 }}>
                  {m.card.shot && (
                    <img src={m.card.shot} alt={`${m.card.host} homepage`} loading="lazy"
                         style={{ display: 'block', width: '100%', height: 150, objectFit: 'cover', objectPosition: 'top center' }} />
                  )}
                  <div style={{ padding: '9px 12px 11px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                      {m.card.logo && <img src={m.card.logo} alt="" width="16" height="16" style={{ borderRadius: 4, flex: '0 0 auto' }} />}
                      <span style={{ fontWeight: 700, fontSize: 13, lineHeight: 1.25 }}>{m.card.title}</span>
                    </div>
                    {m.card.description && (
                      <div style={{ fontSize: 11.5, opacity: .72, marginTop: 3, lineHeight: 1.35 }}>{m.card.description}</div>
                    )}
                    <div style={{ fontSize: 11, opacity: .55, marginTop: 5 }}>
                      {m.card.host}{m.card.phone ? ` · ${m.card.phone}` : ''}
                    </div>
                  </div>
                </a>
              )}
            </React.Fragment>
          ))}
          {sending && (
            <div className="support-chat__bubble support-chat__bubble--assistant" style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
              <span className="support-chat__typing" style={{ display: 'inline-flex', gap: 3 }}><span /><span /><span /></span>
              {/* Say what she's ACTUALLY doing (Braeden 8/13: "it shows what
                  she is trying to find... she needs to think when shes talking
                  not just spit stuff out"). The responder sets this via
                  onThinking; a bare "Thinking" is the fallback. */}
              <ThinkingTimer label={thinkingLabel || 'Thinking'} />
            </div>
          )}
          {micErr && <div className="support-chat__bubble support-chat__bubble--assistant" style={{ opacity: 0.8 }}>{micErr}</div>}

          {/* Persistent primary action (e.g. "Generate a plan"). Enablement:
              readyAction.enabled (boolean) wins when provided — the caller can
              light the button up as soon as ANYTHING is typed (no send needed).
              Otherwise the legacy rule: enabled once a user message was sent. */}
          {readyAction && (() => {
            const ready = typeof readyAction.enabled === 'boolean'
              ? readyAction.enabled
              : (readyAction.requireMessage === false || messages.some((m) => m.role === 'user'));
            return (
            <button
              type="button"
              disabled={readyAction.busy || !ready}
              onClick={() => { try { readyAction.onClick && readyAction.onClick(); } catch (_) {} }}
              style={{
                marginTop: 10, width: '100%', font: 'inherit', fontSize: 15, fontWeight: 800, color: '#fff',
                background: readyAction.busy ? 'linear-gradient(160deg,#4a4a52,#33333a)' : 'linear-gradient(160deg,#00d9ff,#0a84ff)',
                border: 'none', borderRadius: 14, padding: '14px 16px',
                cursor: readyAction.busy ? 'wait' : 'pointer',
                opacity: (!ready && !readyAction.busy) ? 0.45 : 1,
                boxShadow: '0 8px 22px rgba(0,140,255,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
              }}
            >
              {readyAction.busy && <span className="support-chat__typing" style={{ display: 'inline-flex', gap: 3 }}><span /><span /><span /></span>}
              {readyAction.busy
                ? <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>{readyAction.busyLabel || 'Building your plan…'}<span style={{ opacity: 0.85 }}><ThinkingTimer label="" /></span></span>
                : readyAction.label}
            </button>
            );
          })()}
        </div>

        {/* Action buttons — ALWAYS a blue glossy bar directly above the text box */}
        {!checklistDone && messages.length === 0 && (preChoice || (checklist && Array.isArray(checklist.options))) && (
          <div style={{ display: 'flex', gap: 8, padding: '0 16px 10px' }}>
            {preChoice && Array.isArray(preChoice.options)
              ? preChoice.options.map((o) => (
                  <button key={o.id} type="button" className="aria-gloss-btn" onClick={() => confirmChoice(o.id)}
                    style={{ flex: 1, fontSize: 13.5, padding: '12px 12px' }}>{o.label}</button>
                ))
              : (
                  <button type="button" className="aria-gloss-btn" onClick={confirmSetup}
                    style={{ flex: 1, fontSize: 14, padding: '13px 16px' }}>{(checklist && checklist.confirmLabel) || confirmLabel || 'Save'}</button>
                )}
          </div>
        )}

        {plusOpen && (
          <div className="aria-plus-menu" onClick={(e) => e.stopPropagation()}>
            <button type="button" onClick={() => { setPlusOpen(false); try { fileRef.current && fileRef.current.click(); } catch (_) {} }}>
              <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><path d="M17 8l-5-5-5 5" /><path d="M12 3v12" /></svg>
              Upload files
            </button>
            {/* SAME BUTTON ON BOTH SURFACES (Braeden 9/7, see App.jsx's + menu).
                The web arm used to render a DISABLED "Assist (desktop app)",
                which was wrong twice over: onAssist is onScreenShareClick,
                which works in a browser (getDisplayMedia), and the dock next to
                this box already offered it. A dead grey button that contradicts
                the live one beside it is worse than either answer. */}
            <button type="button" onClick={() => { setPlusOpen(false); try { if (onAssist) onAssist(); else window.dispatchEvent(new CustomEvent('aria:openAssist')); } catch (_) {} }}>
              <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="3" width="20" height="14" rx="2" /><path d="M8 21h8M12 17v4" /></svg>
              Assist mode
            </button>
          </div>
        )}
        <input ref={fileRef} type="file" multiple style={{ display: 'none' }} onChange={(e) => {
          const names = Array.from(e.target.files || []).map((f) => f.name);
          if (names.length) setMessages((m) => [...m, { role: 'user', content: '📎 ' + names.join(', ') }]);
          e.target.value = '';
        }} />

        <form className="support-chat__input-row" onSubmit={(e) => { e.preventDefault(); send(); }}>
          <button
            type="button"
            onClick={() => setPlusOpen((v) => !v)}
            aria-label="Add — files or assist mode"
            title="Add files or start assist mode"
            style={{ flex: 'none', width: 38, height: 38, borderRadius: 999, border: 'none', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-2,#888)', background: 'rgba(127,127,127,0.16)', transform: plusOpen ? 'rotate(45deg)' : 'none', transition: 'transform .15s ease' }}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>
          </button>
          <button
            type="button"
            onClick={toggleMic}
            aria-label={recording ? 'Stop recording' : 'Talk to Aria'}
            title={recording ? 'Listening… tap to stop' : 'Talk to Aria'}
            style={{ flex: 'none', width: 38, height: 38, borderRadius: 999, border: 'none', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', color: recording ? '#fff' : 'var(--text-2,#888)', background: recording ? '#ff453a' : 'rgba(127,127,127,0.16)', boxShadow: recording ? '0 0 0 4px rgba(255,69,58,0.25)' : 'none', transition: 'background .15s' }}
          >
            <MicIcon on={recording} />
          </button>
          <input
            ref={inputRef}
            type="text"
            placeholder={recording ? 'Listening…' : (inputPlaceholder || 'Ask Aria, or tap the mic to talk…')}
            value={input}
            onChange={(e) => { setInput(e.target.value); try { if (onDraftChange) onDraftChange(e.target.value); } catch (_) {} }}
          />
          <button type="submit" disabled={!input.trim() || sending} aria-label="Send">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M12 19V5" /><path d="M5 12l7-7 7 7" /></svg>
          </button>
        </form>

      </div>
    </div>,
    document.body
  );
}

// forwardRef wrapper — parents that don't pass a ref are unaffected.
const SetupHelpBox = React.forwardRef(SetupHelpBoxInner);
export default SetupHelpBox;
